import { Group, Paper } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconMessage, IconPackage, IconThumbDown, IconThumbUp } from '@tabler/icons-react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table'
import { useCallback, useState } from 'react'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { Table } from '@/components/Table'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useTopicsEnabled } from '@/hooks/useTopicsEnabled'
import type { FeedbackAnnotation } from '@/services/api/api.types'

import { useFeedbackTableColumns } from './columns'
import { FeedbackDetailModal } from './FeedbackDetailModal'
import { useFeedbackFilters } from './useFeedbackFilters'

const noFeedback: FeedbackAnnotation[] = []

export function FeedbackTable() {
  const { api } = useGlobalState()
  const { topicsEnabled } = useTopicsEnabled()

  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackAnnotation | null>(null)
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false)

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: () => api.getSponsorTopics(),
    staleTime: 5 * 60 * 1000,
  })

  const tagsQuery = useQuery({
    queryKey: ['feedback', 'tags'],
    queryFn: () => api.getFeedbackTagList(),
    staleTime: 5 * 60 * 1000,
  })

  const filters = useFeedbackFilters(columnFilters, sorting)

  const { data, isLoading } = useQuery({
    queryKey: ['feedback', 'list', pagination, filters],
    queryFn: () =>
      api.getFeedback({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        ...filters,
      }),
    placeholderData: keepPreviousData,
  })

  const handleViewDetails = useCallback(
    (feedback: FeedbackAnnotation) => {
      setSelectedFeedback(feedback)
      openModal()
    },
    [openModal],
  )

  const columns = useFeedbackTableColumns({
    topicsData: topicsQuery.data || [],
    contentModulesData: data?.stats?.byContentModule || {},
    byTopicData: data?.stats?.byTopic || {},
    tagsData: tagsQuery.data || [],
    onViewDetails: handleViewDetails,
    topicsEnabled,
  })

  return (
    <>
      {/* Aggregated Stats */}
      {data?.stats && (
        <Paper p="lg" mb="md" shadow="sm" radius="md" px="xl">
          <Group gap="xl" justify="flex-start" align="top">
            <StatInfoGraphic
              icon={IconPackage}
              label="Modules"
              value={data.stats.modules}
              tooltipText="Number of modules with feedback"
            />
            <StatInfoGraphic
              icon={IconMessage}
              label="Comments"
              value={data.stats.comments}
              tooltipText="Total number of feedback comments"
            />
            <StatInfoGraphic
              icon={IconThumbUp}
              label="Thumbs Up"
              value={data.stats.thumbsUp}
              tooltipText="Number of positive feedback"
            />
            <StatInfoGraphic
              icon={IconThumbDown}
              label="Thumbs Down"
              value={data.stats.thumbsDown}
              tooltipText="Number of negative feedback"
            />
          </Group>
        </Paper>
      )}

      <Paper p="lg" shadow="sm" radius="md">
        <Table
          columns={columns}
          data={data?.results ?? noFeedback}
          isLoading={isLoading}
          entity={{ singular: 'Feedback', plural: 'Feedback', Icon: IconMessage }}
          enableGlobalFilter={false}
          manualFiltering
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          enableSorting
          enableMultiSort={false}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={{ state: pagination, rowCount: data?.total ?? 0, onChange: setPagination }}
        />
      </Paper>

      <FeedbackDetailModal
        opened={modalOpened}
        onClose={closeModal}
        feedback={selectedFeedback}
        topicsData={topicsQuery.data || []}
      />
    </>
  )
}
